import React from 'react';

import { renderToStaticMarkup } from 'react-dom/server';

export type Range = {
  offset: number;
  length: number;
  style?: string;
  key?: number;
};

export type RawBlock = {
  key: string;
  text: string;
  type: string;
  depth: number;
  inlineStyleRanges: Range[];
  entityRanges: Range[];
  data?: Record<string, unknown>;
};

export type RawEntity = {
  type: string;
  mutability?: string;
  data: any;
};

export type RawDraftContentState = {
  blocks: RawBlock[];
  entityMap: Record<string, RawEntity>;
};

export type TFunction = (key: string, options?: any) => string;

const VOID_TAGS = [
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'param',
  'source',
  'track',
  'wbr',
];

type StartEnd = { start: string; end: string };

const splitReactElement = (element: React.ReactElement): string | StartEnd => {
  if (VOID_TAGS.includes(element.type as string)) {
    return renderToStaticMarkup(element);
  }
  const tags = renderToStaticMarkup(
    React.cloneElement(element, {}, '\r'),
  ).split('\r');
  return { start: tags[0], end: tags[1] };
};

const hasChildren = (element: unknown): boolean =>
  React.isValidElement(element) &&
  React.Children.count((element.props as any).children) > 0;

const getElementHTML = (element: any, text: string | null = null): any => {
  if (element == null) {
    return element;
  }
  if (typeof element === 'string') {
    return element;
  }
  if (React.isValidElement(element)) {
    if (hasChildren(element)) {
      return renderToStaticMarkup(element);
    }
    const tags = splitReactElement(element);
    if (text != null && typeof tags === 'object') {
      return tags.start + text + tags.end;
    }
    return tags;
  }
  if (text != null) {
    return element.start + text + element.end;
  }
  return element;
};

const getElementTagLength = (
  element: any,
  type: 'start' | 'end' = 'start',
): number => {
  if (React.isValidElement(element)) {
    const splitElement = splitReactElement(element);
    if (typeof splitElement === 'string') {
      return 0;
    }
    const length = splitElement[type].length;
    const child = React.Children.toArray((element.props as any).children)[0];
    return (
      length +
      (child && React.isValidElement(child)
        ? getElementTagLength(child, type)
        : 0)
    );
  }
  if (typeof element === 'object') {
    return element[type] ? element[type].length : 0;
  }
  return 0;
};

const getBlockTags = (blockHTML: any): any => {
  if (typeof blockHTML === 'string') {
    return blockHTML;
  }
  if (React.isValidElement(blockHTML)) {
    if (hasChildren(blockHTML)) {
      return renderToStaticMarkup(blockHTML);
    }
    return splitReactElement(blockHTML);
  }
  if (
    Object.prototype.hasOwnProperty.call(blockHTML, 'element') &&
    React.isValidElement(blockHTML.element)
  ) {
    return Object.assign({}, blockHTML, splitReactElement(blockHTML.element));
  }
  return blockHTML;
};

const getNestedBlockTags = (blockHTML: any, depth: number): any => {
  const { nest } = blockHTML;
  if (typeof nest === 'function' || React.isValidElement(nest)) {
    const { start, end } = splitReactElement(
      typeof nest === 'function' ? nest(depth) : nest,
    ) as StartEnd;
    return Object.assign({}, blockHTML, { nestStart: start, nestEnd: end });
  }
  return blockHTML;
};

const rangeSort = (r1: Range, r2: Range): number => {
  if (r1.offset === r2.offset) {
    return r2.length - r1.length;
  }
  return r1.offset - r2.offset;
};

const updateMutation = (
  mutation: Range,
  originalOffset: number,
  originalLength: number,
  newLength: number,
  prefixLength: number,
  suffixLength: number,
): Range | Range[] => {
  const lengthDiff = newLength - originalLength;
  const mutationAfterChange =
    originalOffset + originalLength <= mutation.offset;
  if (mutationAfterChange) {
    return Object.assign({}, mutation, {
      offset: mutation.offset + lengthDiff,
    });
  }
  const mutationContainsChange =
    originalOffset >= mutation.offset &&
    originalOffset + originalLength <= mutation.offset + mutation.length;
  if (mutationContainsChange) {
    return Object.assign({}, mutation, {
      length: mutation.length + lengthDiff,
    });
  }
  const mutationWithinPrefixChange =
    mutation.offset >= originalOffset &&
    mutation.offset + mutation.length <= originalOffset + originalLength &&
    prefixLength > 0;
  if (mutationWithinPrefixChange) {
    return Object.assign({}, mutation, {
      offset: mutation.offset + prefixLength,
    });
  }
  const mutationContainsPrefix =
    mutation.offset < originalOffset &&
    mutation.offset + mutation.length <= originalOffset + originalLength &&
    mutation.offset + mutation.length > originalOffset &&
    prefixLength > 0;
  if (mutationContainsPrefix) {
    return [
      Object.assign({}, mutation, {
        length: originalOffset - mutation.offset,
      }),
      Object.assign({}, mutation, {
        offset: originalOffset + prefixLength,
        length: mutation.offset - originalOffset + mutation.length,
      }),
    ];
  }
  const mutationContainsSuffix =
    mutation.offset >= originalOffset &&
    mutation.offset + mutation.length > originalOffset + originalLength &&
    originalOffset + originalLength > mutation.offset &&
    suffixLength > 0;
  if (mutationContainsSuffix) {
    return [
      Object.assign({}, mutation, {
        offset: mutation.offset + prefixLength,
        length: originalOffset + originalLength - mutation.offset,
      }),
      Object.assign({}, mutation, {
        offset: originalOffset + originalLength + prefixLength + suffixLength,
        length:
          mutation.offset + mutation.length - (originalOffset + originalLength),
      }),
    ];
  }
  return mutation;
};

const ENTITY_MAP: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#x27;',
  '`': '&#x60;',
  '\n': '<br/>',
};

export const escapeText = (text: string): string =>
  Array.from(text)
    .map(ch => ENTITY_MAP[ch] ?? ch)
    .join('');

export const escapeAttr = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const encodeBlock = (block: RawBlock): RawBlock => {
  const blockText = block.text.split('');
  let entities = (block.entityRanges ?? []).slice().sort(rangeSort);
  let styles = (block.inlineStyleRanges ?? []).slice().sort(rangeSort);
  let resultText = '';

  for (const char of blockText) {
    if (ENTITY_MAP[char] !== undefined) {
      const encoded = ENTITY_MAP[char];
      const resultIndex = resultText.length;
      resultText += encoded;
      const updateForChar = (mutation: Range): Range =>
        updateMutation(
          mutation,
          resultIndex,
          char.length,
          encoded.length,
          0,
          0,
        ) as Range;
      entities = entities.map(updateForChar);
      styles = styles.map(updateForChar);
    } else {
      resultText += char;
    }
  }

  return Object.assign({}, block, {
    text: resultText,
    inlineStyleRanges: styles,
    entityRanges: entities,
  });
};

const blockEntities = (
  block: RawBlock,
  entityMap: Record<string, RawEntity>,
  entityConverter: (entity: RawEntity, originalText: string) => any,
): RawBlock => {
  let resultText = block.text.split('');

  if (
    Object.prototype.hasOwnProperty.call(block, 'entityRanges') &&
    block.entityRanges.length > 0
  ) {
    let entities: Range[] = block.entityRanges.slice().sort(rangeSort);
    let styles: Range[] = block.inlineStyleRanges;

    for (let index = 0; index < entities.length; index++) {
      const entityRange = entities[index];
      const entity = entityMap[entityRange.key as number];
      const originalText = resultText
        .slice(entityRange.offset, entityRange.offset + entityRange.length)
        .join('');
      const entityHTML =
        entity == null ? originalText : entityConverter(entity, originalText);
      const elementHTML = getElementHTML(entityHTML, originalText);
      const converted: string[] | string =
        elementHTML != null ? elementHTML.split('') : originalText;
      const prefixLength = getElementTagLength(entityHTML, 'start');
      const suffixLength = getElementTagLength(entityHTML, 'end');

      const updateLaterMutation = (
        mutation: Range,
        mutationIndex: number,
      ): Range | Range[] => {
        if (
          mutationIndex > index ||
          Object.prototype.hasOwnProperty.call(mutation, 'style')
        ) {
          return updateMutation(
            mutation,
            entityRange.offset,
            entityRange.length,
            (converted as ArrayLike<unknown>).length,
            prefixLength,
            suffixLength,
          );
        }
        return mutation;
      };

      const updateLaterMutations = (mutationList: Range[]): Range[] =>
        mutationList.flatMap((mutation, mutationIndex) => {
          const updatedMutation = updateLaterMutation(mutation, mutationIndex);
          return Array.isArray(updatedMutation)
            ? updatedMutation
            : [updatedMutation];
        });

      entities = updateLaterMutations(entities);
      styles = updateLaterMutations(styles);
      resultText = [
        ...resultText.slice(0, entityRange.offset),
        ...(typeof converted === 'string' ? converted.split('') : converted),
        ...resultText.slice(entityRange.offset + entityRange.length),
      ];
    }

    return Object.assign({}, block, {
      text: resultText.join(''),
      inlineStyleRanges: styles,
      entityRanges: entities,
    });
  }

  return block;
};

const defaultInlineHTML = (style: string): React.ReactElement | StartEnd => {
  switch (style) {
    case 'BOLD':
      return React.createElement('strong', null);
    case 'ITALIC':
      return React.createElement('em', null);
    case 'UNDERLINE':
      return React.createElement('u', null);
    case 'CODE':
      return React.createElement('code', null);
    default:
      return { start: '', end: '' };
  }
};

const subtractStyles = (original: Range[], toRemove: Range[]): Range[] =>
  original.filter(
    el => !toRemove.some(elToRemove => elToRemove.style === el.style),
  );

const popEndingStyles = (styleStack: Range[], endingStyles: Range[]): Range[] =>
  endingStyles.reduceRight((stack, _style) => stack.slice(0, -1), styleStack);

const characterStyles = (offset: number, ranges: Range[]): Range[] =>
  ranges.filter(
    range => offset >= range.offset && offset < range.offset + range.length,
  );

const rangeIsSubset = (firstRange: Range, secondRange: Range): boolean => {
  const secondStartWithinFirst = firstRange.offset <= secondRange.offset;
  const secondEndWithinFirst =
    firstRange.offset + firstRange.length >=
    secondRange.offset + secondRange.length;
  return secondStartWithinFirst && secondEndWithinFirst;
};

const latestStyleLast = (s1: Range, s2: Range): number => {
  const s2endIndex = s2.offset + s2.length;
  const s1endIndex = s1.offset + s1.length;
  return s2endIndex - s1endIndex;
};

const getStylesToReset = (
  remainingStyles: Range[],
  newStyles: Range[],
): Range[] => {
  const firstStyleToReset = remainingStyles.findIndex(
    remainingStyle =>
      !newStyles.every(newStyle => rangeIsSubset(remainingStyle, newStyle)),
  );
  return firstStyleToReset === -1
    ? []
    : remainingStyles.slice(firstStyleToReset);
};

const blockInlineStyles = (block: RawBlock): string => {
  let result = '';
  let styleStack: Range[] = [];
  const sortedRanges = block.inlineStyleRanges.slice().sort(rangeSort);
  const originalTextArray = block.text.split('');

  const appendStartMarkup = (string: string, styleRange: Range): string =>
    string +
    (getElementHTML(defaultInlineHTML(styleRange.style as string)) as StartEnd)
      .start;
  const prependEndMarkup = (string: string, styleRange: Range): string =>
    (getElementHTML(defaultInlineHTML(styleRange.style as string)) as StartEnd)
      .end + string;

  for (let i = 0; i < originalTextArray.length; i++) {
    const styles = characterStyles(i, sortedRanges);
    const endingStyles = subtractStyles(styleStack, styles);
    const newStyles = subtractStyles(styles, styleStack);
    const remainingStyles = subtractStyles(styleStack, endingStyles);
    const resetStyles = getStylesToReset(remainingStyles, newStyles);
    const openingStyles = resetStyles.concat(newStyles).sort(latestStyleLast);
    const openingStyleTags = openingStyles.reduce(appendStartMarkup, '');
    const endingStyleTags = endingStyles
      .concat(resetStyles)
      .reduce(prependEndMarkup, '');
    result += endingStyleTags + openingStyleTags + originalTextArray[i];
    styleStack = popEndingStyles(styleStack, resetStyles.concat(endingStyles));
    styleStack = styleStack.concat(openingStyles);
  }

  result = styleStack.reduceRight(
    (res, openStyle) =>
      res +
      (getElementHTML(defaultInlineHTML(openStyle.style as string)) as StartEnd)
        .end,
    result,
  );
  return result;
};

export const defaultBlockHTML: Record<string, any> = {
  unstyled: React.createElement('p', null),
  paragraph: React.createElement('p', null),
  'header-one': React.createElement('h1', null),
  'header-two': React.createElement('h2', null),
  'header-three': React.createElement('h3', null),
  'header-four': React.createElement('h4', null),
  'header-five': React.createElement('h5', null),
  'header-six': React.createElement('h6', null),
  'code-block': React.createElement('pre', null),
  blockquote: React.createElement('blockquote', null),
  'unordered-list-item': {
    element: React.createElement('li', null),
    nest: React.createElement('ul', null),
  },
  'ordered-list-item': {
    element: React.createElement('li', null),
    nest: (depth: number) => {
      const ORDERED_LIST_TYPES = ['1', 'a', 'i'];
      const type = ORDERED_LIST_TYPES[depth % 3];
      return React.createElement('ol', { type });
    },
  },
  media: React.createElement('figure', null),
  atomic: React.createElement('figure', null),
};

export const raBlockToHTML = (block: RawBlock): any => {
  switch (block.type) {
    case 'unstyled':
      return block.text === ' ' || block.text === ''
        ? React.createElement('br', null)
        : React.createElement('div', null);
    case 'paragraph':
      return React.createElement('p', null);
    case 'atomic':
      if (block.text.trim() !== '') {
        return React.createElement('img', {
          src: block.text,
          style: {
            maxWidth: 256,
            maxHeight: 128,
            objectFit: 'contain',
          },
        });
      }
      return { start: '<div>', end: '</div>' };
    default:
      return undefined;
  }
};

export const raGetBlockHTML = (block: RawBlock): any => {
  return raBlockToHTML(block) ?? defaultBlockHTML[block.type];
};

// Draft raw ranges count code points (Draft's UnicodeUtils.strlen) while the
// converter below indexes UTF-16 units, so an astral character (emoji) before a
// range shifted it and could split its surrogate pair across a tag.
const withUtf16Ranges = (block: RawBlock): RawBlock => {
  const unitIndex = [0];
  for (const char of block.text) {
    unitIndex.push(unitIndex[unitIndex.length - 1] + char.length);
  }
  if (unitIndex.length - 1 === block.text.length) {
    return block;
  }
  const toUnits = (codePoints: number): number =>
    unitIndex[Math.min(Math.max(codePoints, 0), unitIndex.length - 1)];
  const convert = (range: Range): Range => ({
    ...range,
    offset: toUnits(range.offset),
    length: toUnits(range.offset + range.length) - toUnits(range.offset),
  });
  return {
    ...block,
    entityRanges: (block.entityRanges ?? []).map(convert),
    inlineStyleRanges: (block.inlineStyleRanges ?? []).map(convert),
  };
};

export const renderBlocks = (
  rawState: RawDraftContentState,
  getBlockHTML: (block: RawBlock) => any,
  entityToHTML: (entity: RawEntity, originalText: string) => any,
): string => {
  const entityMap = rawState.entityMap ?? {};
  let listStack: RawBlock[] = [];

  let result = (rawState.blocks ?? [])
    .map(block => {
      const { type, depth } = block;
      let closeNestTags = '';
      let openNestTags = '';
      const blockHTMLResult = getBlockHTML(block);

      if (!blockHTMLResult) {
        throw new Error(
          `convertToHTML: missing HTML definition for block with type ${block.type}`,
        );
      }

      if (!blockHTMLResult.nest) {
        closeNestTags = listStack.reduceRight(
          (string, nestedBlock) =>
            string +
            getNestedBlockTags(getBlockHTML(nestedBlock), depth).nestEnd,
          '',
        );
        listStack = [];
      } else {
        while (
          depth + 1 !== listStack.length ||
          type !== listStack[depth].type
        ) {
          if (depth + 1 === listStack.length) {
            const blockToClose = listStack[depth];
            closeNestTags += getNestedBlockTags(
              getBlockHTML(blockToClose),
              depth,
            ).nestEnd;
            openNestTags += getNestedBlockTags(
              getBlockHTML(block),
              depth,
            ).nestStart;
            listStack[depth] = block;
          } else if (depth + 1 < listStack.length) {
            const blockToClose = listStack[listStack.length - 1];
            closeNestTags += getNestedBlockTags(
              getBlockHTML(blockToClose),
              depth,
            ).nestEnd;
            listStack = listStack.slice(0, -1);
          } else {
            openNestTags += getNestedBlockTags(
              getBlockHTML(block),
              depth,
            ).nestStart;
            listStack.push(block);
          }
        }
      }

      const innerHTML = blockInlineStyles(
        blockEntities(
          encodeBlock(withUtf16Ranges(block)),
          entityMap,
          entityToHTML,
        ),
      );
      const blockHTML = getBlockTags(getBlockHTML(block));
      let html: string;

      if (typeof blockHTML === 'string') {
        html = blockHTML;
      } else {
        html = blockHTML.start + innerHTML + blockHTML.end;
      }

      if (
        innerHTML.length === 0 &&
        Object.prototype.hasOwnProperty.call(blockHTML, 'empty')
      ) {
        if (React.isValidElement(blockHTML.empty)) {
          html = renderToStaticMarkup(blockHTML.empty);
        } else {
          html = blockHTML.empty;
        }
      }

      return closeNestTags + openNestTags + html;
    })
    .join('');

  result = listStack.reduce(
    (res, nestBlock) =>
      res +
      getNestedBlockTags(getBlockHTML(nestBlock), nestBlock.depth).nestEnd,
    result,
  );

  return result;
};
